/* eslint-disable class-methods-use-this */
import { UUID } from "../../types";
import {
  MessagingError,
  isAuthRejection,
  isUnrecoverableServerError,
} from "./error";
import WebSocketClient from "./websocket-client";
import * as Sentry from "@sentry/browser";

export interface MessageEventMap {
  token_registered: boolean;
  requesting_notification_permission: boolean;
  notification_enabled: boolean;
  message: any;
}

export type OnMessageCallback = <K extends keyof MessageEventMap>(
  event: K,
  message: MessageEventMap[K],
) => void;

export type OnErrorCallback = (error: string) => void;

export type MessagingType = "websocket";

export interface MessagingOptions {
  messagingType: MessagingType;
}

export interface InitOptions {
  userId: UUID;
  deviceId: UUID;
}

export default class Messaging {
  private userId: UUID | null = null;

  private deviceId: UUID | null = null;

  private messagingType: MessagingType = "websocket";

  private websocketClient: WebSocketClient | null = null;

  private onMessageCallback: OnMessageCallback | null = null;

  private onAuthFailureCallback: ((message: string) => void) | null = null;

  private onErrorCallback: OnErrorCallback | null = null;

  isDeviceRegistered: boolean = false;

  constructor(options: MessagingOptions = { messagingType: "websocket" }) {
    this.messagingType = options.messagingType;
  }

  async init({ userId, deviceId }: InitOptions) {
    this.userId = userId;
    this.deviceId = deviceId;
    try {
      await this.initWebsocket();
    } catch (error: any) {
      Sentry.captureException(error);
      if (error instanceof MessagingError) {
        this.captureError(error.message);
      }
    }
  }

  async initWebsocket() {
    if (!this.userId) {
      throw new MessagingError(
        "User Id is required to establishing websocket connection",
      );
    }

    if (!this.deviceId) {
      throw new MessagingError(
        "Device Id is required to establishing websocket connection",
      );
    }

    this.websocketClient = new WebSocketClient({
      url: `${process.env.REACT_APP_WEBSOCKET_URL}`,
      userId: this.userId,
      debug: console.log,
      deviceId: this.deviceId,
    });

    this.websocketClient.onRegisterDevice((isDeviceRegistered) => {
      //this.isDeviceRegistered = isDeviceRegistered;
    });

    // The device is considered registered only once the backend confirms it by pushing a DEVICE_REGISTER message.
    this.websocketClient.onMessage((data) => {
      if (data?.push_type === "DEVICE_REGISTER") {
        this.isDeviceRegistered = true;
      }

      this.onMessageCallback?.("message", data);
    });

    this.websocketClient.onError((error) => {
      if (!this.onErrorCallback) return;

      let errorMessage = "";

      // 1. Unpack structured errors safely from the client layer
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = String((error as any).message);
      } else {
        errorMessage = String(error);
      }

      // 2. Clear out formatting residues
      errorMessage = errorMessage.replace(/\\c/g, "").trim();

      // 3. Auth rejections are expected: stop the reconnect loop and skip Sentry.
      if (isAuthRejection(errorMessage)) {
        console.warn("🔒 WebSocket Authentication Failed.");

        this.websocketClient?.forceShutdown();
        this.websocketClient = null;

        // Let the app react (e.g. toast the server message).
        this.onAuthFailureCallback?.("");

        return;
      }

      if (isUnrecoverableServerError(errorMessage)) {
        this.websocketClient?.handleUnrecoverableStompError(errorMessage);
        this.onAuthFailureCallback?.("");
        return;
      }

      Sentry.captureException(
        new Error(`WebSocket System Error: ${errorMessage}`),
        {
          tags: {
            userId: this.userId || "unknown",
            deviceId: this.deviceId || "unknown",
          },
        },
      );

      this.onErrorCallback(errorMessage);
    });

    this.websocketClient.connect();
  }

  isNotificationSupported() {
    return "Notification" in window;
  }

  // Resolves once the device has been registered over the websocket, or `false`
  waitUntilDeviceRegistered(
    timeoutMs: number = 5000,
    intervalMs: number = 100,
  ): Promise<boolean> {
    if (this.isDeviceRegistered) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      const timer = setInterval(() => {
        if (this.isDeviceRegistered) {
          clearInterval(timer);
          resolve(true);
        } else if (Date.now() - startedAt >= timeoutMs) {
          clearInterval(timer);
          resolve(false);
        }
      }, intervalMs);
    });
  }

  destroyWebsocket() {
    this.websocketClient?.destroy();
    this.websocketClient = null;
  }

  destroy() {
    this.destroyWebsocket();

    this.userId = null;
    this.deviceId = null;
    this.isDeviceRegistered = false;
    this.onMessageCallback = null;
    this.onErrorCallback = null;
    this.onAuthFailureCallback = null;
  }

  onMessage(onMessageCallback: OnMessageCallback) {
    this.onMessageCallback = onMessageCallback;
  }

  onError(onErrorCallback: OnErrorCallback) {
    this.onErrorCallback = onErrorCallback;
  }

  onAuthFailure(onAuthFailureCallback: (message: string) => void) {
    this.onAuthFailureCallback = onAuthFailureCallback;
  }

  private captureError(errorMsg: string) {
    this.onErrorCallback?.(errorMsg);
  }
}
